import { useMemo, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { toast } from 'sonner'
import { ArrowLeft, ExternalLink } from 'lucide-react'
import {
  Button,
  Chip,
  EmptyState,
  PageHeaderBand,
} from '@/components/ui'
import { odooUrl } from '@/lib/utils/odoo-url'
import {
  useApproveImpacts,
  useSetCollisionStatus,
  useSetFlagStatus,
  useVendorPriceChangeFlag,
  useVendorPriceChangeImpact,
} from '../api/queries'
import type { CustomerImpact, PoCollision } from '../api/types'

/**
 * One flagged vendor price change: the affected-customer table with
 * server-computed margin-preserving suggestions, editable before
 * approval, and the resolve/dismiss action for the flag itself.
 */
export function VendorPriceChangeDetailPage() {
  const params = useParams({ strict: false }) as { flagId?: string }
  const flagIdNum = Number(params.flagId)
  const navigate = useNavigate()

  const { data: flag, isLoading: flagLoading, isError: flagError } = useVendorPriceChangeFlag(flagIdNum)
  const { data: impact, isLoading: impactLoading, isError: impactError, refetch } =
    useVendorPriceChangeImpact(flagIdNum)

  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [overrides, setOverrides] = useState<Record<number, string>>({})

  const impacts = useMemo<CustomerImpact[]>(() => impact?.impacts ?? [], [impact])
  const pendingImpacts = useMemo(() => impacts.filter((i) => i.status === 'pending'), [impacts])

  const approve = useApproveImpacts()
  const setStatus = useSetFlagStatus()
  const setCollisionStatus = useSetCollisionStatus()

  const poCollisions = useMemo<PoCollision[]>(() => impact?.po_collisions ?? [], [impact])

  function resolveCollision(collisionId: number, status: 'resolved' | 'dismissed') {
    setCollisionStatus.mutate(
      { flagId: flagIdNum, collisionId, status },
      {
        onSuccess: () => toast.success(status === 'resolved' ? 'Collision resolved.' : 'Collision dismissed.'),
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to update collision.'),
      },
    )
  }

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function approveSelected() {
    if (selected.size === 0) return
    const price_overrides: Record<string, number> = {}
    for (const id of selected) {
      const raw = overrides[id]
      if (raw && raw.trim()) price_overrides[String(id)] = Number(raw)
    }
    approve.mutate(
      { flagId: flagIdNum, impact_ids: Array.from(selected), price_overrides },
      {
        onSuccess: (result) => {
          if (result.failed.length > 0) {
            toast.error(`${result.written.length} written, ${result.failed.length} failed — see console.`)
            console.error('approve_customer_impacts failures', result.failed)
          } else {
            toast.success(`Wrote ${result.written.length} customer price${result.written.length === 1 ? '' : 's'}.`)
          }
          setSelected(new Set())
          void refetch()
        },
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Approval failed.'),
      },
    )
  }

  function resolveFlag(status: 'resolved' | 'dismissed') {
    setStatus.mutate(
      { flagId: flagIdNum, status },
      {
        onSuccess: () => {
          toast.success(status === 'resolved' ? 'Flag resolved.' : 'Flag dismissed.')
          navigate({ to: '/vendor-price-changes' })
        },
      },
    )
  }

  if (flagLoading || impactLoading) {
    return <div className="p-6 text-sm text-tertiary">Loading…</div>
  }
  if (flagError || !flag) {
    return (
      <div className="p-6">
        <EmptyState variant="error" title="Couldn't load this flag" description="Try going back to the queue." />
      </div>
    )
  }

  const isTerminal = flag.status === 'resolved' || flag.status === 'dismissed'

  return (
    <div data-density="compact" className="flex h-full flex-col">
      <PageHeaderBand
        title={`${impact?.product_name ?? flag.product_name ?? 'Product'} — ${impact?.vendor_name ?? flag.vendor_name ?? 'Vendor'}`}
        description={`Tier ${flag.tier_key} · ${flag.old_price.toLocaleString(undefined, { style: 'currency', currency: 'USD' })} → ${flag.new_price.toLocaleString(undefined, { style: 'currency', currency: 'USD' })} (${flag.pct_change > 0 ? '+' : ''}${flag.pct_change.toFixed(1)}%)`}
      >
        <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/vendor-price-changes' })}>
          <ArrowLeft size={12} /> Back to queue
        </Button>
        <Chip size="md" variant={isTerminal ? 'neutral' : 'accent'} className="ml-2">
          {flag.status}
        </Chip>
        {!isTerminal ? (
          <div className="ml-auto flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => resolveFlag('dismissed')} loading={setStatus.isPending}>
              Dismiss
            </Button>
            <Button variant="secondary" size="sm" onClick={() => resolveFlag('resolved')} loading={setStatus.isPending}>
              Mark resolved
            </Button>
          </div>
        ) : null}
      </PageHeaderBand>

      <div className="flex-1 min-h-0 overflow-auto bg-canvas p-4">
        {impactError ? (
          <EmptyState variant="error" title="Couldn't load affected customers" description="Try refreshing." />
        ) : impacts.length === 0 ? (
          <EmptyState
            variant="no-data"
            title="No customers on this tier"
            description="No customer with pricing on this product, or a purchase in the last 730 days, buys at the tier that changed."
          />
        ) : (
          <div className="overflow-x-auto rounded-sm border border-border-subtle bg-surface">
            <table className="w-full border-collapse text-sm">
              <thead className="border-b border-border-subtle bg-surface-sunk">
                <tr className="text-left">
                  <Th />
                  <Th>Customer</Th>
                  <Th align="right">Current price</Th>
                  <Th align="right">Margin</Th>
                  <Th align="right">Suggested price</Th>
                  <Th align="right">Approved price</Th>
                  <Th>Last order</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {impacts.map((row) => (
                  <tr key={row.impact_id} className="border-b border-border-subtle last:border-0">
                    <Td>
                      {row.status === 'pending' ? (
                        <input
                          type="checkbox"
                          checked={selected.has(row.impact_id)}
                          onChange={() => toggle(row.impact_id)}
                        />
                      ) : null}
                    </Td>
                    <Td>
                      <div className="flex items-center gap-1.5">
                        <span>{row.partner_name ?? `#${row.partner_id}`}</span>
                        {row.needs_new_pricelist ? (
                          <Chip size="xs" variant="info" title="No pricelist item today — approving will create one">
                            New pricelist
                          </Chip>
                        ) : null}
                      </div>
                    </Td>
                    <Td align="right">
                      {row.customer_last_price != null
                        ? row.customer_last_price.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
                        : '—'}
                    </Td>
                    <Td align="right">
                      {row.old_margin_pct != null ? `${(row.old_margin_pct * 100).toFixed(1)}%` : '—'}
                    </Td>
                    <Td align="right">
                      {row.suggested_price != null
                        ? row.suggested_price.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
                        : row.warning ?? '—'}
                    </Td>
                    <Td align="right">
                      {row.status === 'pending' ? (
                        <input
                          type="number"
                          className="w-24 rounded-xs border border-border-default bg-surface px-1.5 py-0.5 text-right text-sm"
                          placeholder={row.suggested_price?.toFixed(2) ?? ''}
                          value={overrides[row.impact_id] ?? ''}
                          onChange={(e) =>
                            setOverrides((prev) => ({ ...prev, [row.impact_id]: e.target.value }))
                          }
                        />
                      ) : row.approved_price != null ? (
                        row.approved_price.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
                      ) : (
                        '—'
                      )}
                    </Td>
                    <Td>
                      {row.last_order_date ? new Date(row.last_order_date).toLocaleDateString() : '—'}
                    </Td>
                    <Td>
                      <span className="text-2xs uppercase text-secondary">{row.status}</span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4">
          <p className="text-role-section-label mb-2">
            Open POs affected {poCollisions.length > 0 ? `(${poCollisions.length})` : ''}
          </p>
          {poCollisions.length === 0 ? (
            <EmptyState
              variant="no-data"
              title="No open POs at the stale price"
              description="No still-open purchase order is currently priced against the old vendor cost for this tier."
            />
          ) : (
            <div className="overflow-x-auto rounded-sm border border-border-subtle bg-surface">
              <table className="w-full border-collapse text-sm">
                <thead className="border-b border-border-subtle bg-surface-sunk">
                  <tr className="text-left">
                    <Th>PO</Th>
                    <Th align="right">PO price</Th>
                    <Th align="right">Current price</Th>
                    <Th align="right">Qty open</Th>
                    <Th>Status</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {poCollisions.map((c) => (
                    <tr key={c.id} className="border-b border-border-subtle last:border-0">
                      <Td>
                        <a
                          href={odooUrl('purchase.order', c.purchase_order_id)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-link hover:underline"
                        >
                          {c.po_name ?? `#${c.purchase_order_id}`} <ExternalLink size={10} />
                        </a>
                      </Td>
                      <Td align="right">{c.po_price.toLocaleString(undefined, { style: 'currency', currency: 'USD' })}</Td>
                      <Td align="right">{c.current_price.toLocaleString(undefined, { style: 'currency', currency: 'USD' })}</Td>
                      <Td align="right">{c.qty_open != null ? c.qty_open.toLocaleString() : '—'}</Td>
                      <Td>
                        <span className="text-2xs uppercase text-secondary">{c.status}</span>
                      </Td>
                      <Td align="right">
                        {c.status === 'pending' ? (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => resolveCollision(c.id, 'dismissed')}
                              loading={setCollisionStatus.isPending}
                            >
                              Dismiss
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => resolveCollision(c.id, 'resolved')}
                              loading={setCollisionStatus.isPending}
                            >
                              Resolve
                            </Button>
                          </div>
                        ) : null}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {impact && impact.monitor_only_customers.length > 0 ? (
          <div className="mt-4 rounded-sm border border-border-subtle bg-surface-sunk p-3 text-sm">
            <p className="text-role-section-label mb-2">
              Monitor only — formula / shared pricelist ({impact.monitor_only_customers.length})
            </p>
            <ul className="flex flex-col gap-1">
              {impact.monitor_only_customers.map((c) => (
                <li key={c.partner_id} className="text-secondary">
                  {c.partner_name ?? `#${c.partner_id}`}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      {pendingImpacts.length > 0 ? (
        <div className="flex items-center justify-end gap-2 border-t border-border-subtle bg-surface px-4 py-3">
          <span className="text-sm text-secondary">{selected.size} selected</span>
          <Button
            variant="primary"
            size="sm"
            disabled={selected.size === 0}
            loading={approve.isPending}
            onClick={approveSelected}
          >
            Approve &amp; write to Odoo
          </Button>
        </div>
      ) : null}
    </div>
  )
}

function Th({ children, align = 'left' }: { children?: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th className={`px-3 py-2 text-role-table-header ${align === 'right' ? 'text-right' : 'text-left'}`}>
      {children}
    </th>
  )
}

function Td({ children, align = 'left' }: { children?: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <td className={`px-3 py-2 text-role-table-cell ${align === 'right' ? 'text-right' : 'text-left'}`}>
      {children}
    </td>
  )
}
